/*
==================================================
ONE OF ONE Product Badge Overlay System - JavaScript
==================================================
Automatically detects products in specified collections
and adds "One of One" badges to product images
==================================================
*/

class ProductBadgeOverlay {
  constructor(options = {}) {
    // Configuration - customize these collection handles as needed
    this.config = {
      // Collection handles that should trigger the badge
      // Add your collection handles here (lowercase, hyphenated)
      targetCollections: [
        'great-white-shark-teeth',
        'rare-jewelry-collection',
        'one-of-one',
        'unique-pieces',
        'limited-edition',
        'exclusive',
        'vintage-originals'
      ],
      
      // Priority-based badge system (higher priority = higher number)
      // Tag-based badges with priority order
      badgeHierarchy: [
        { tag: 'extremely-rare', badge: 'badge-extremely-rare.png', priority: 5 },
        { tag: 'extremely-limited', badge: 'badge-extremely-limited.png', priority: 4 },
        { tag: 'limited', badge: 'badge-limited.png', priority: 3 },
        { tag: 'one-of-one', badge: 'one-of-one-product-badge.png', priority: 2 }, // Your existing badge
        { tag: 'collectors-item', badge: 'badge-collectors-item.png', priority: 1 }
      ],
      
      // Fallback badge for collection-only matching (no specific tags)
      fallbackBadgeImagePath: 'one-of-one-product-badge.png', // Your existing badge file
      
      // Selectors for different product layouts
      productSelectors: [
        '.product-item',
        '.product-card', 
        '.product-grid-item',
        '.card-product',
        '.grid-product',
        '.product-wrap',
        '.product',
        '[data-product-item]',
        '.card-wrapper',        // Dawn theme
        '.card',                // Dawn theme
        'li[id*="Slide-template"]', // Dawn theme product slides
        '.grid__item'           // Common Shopify pattern
      ],
      
      // Image container selectors
      imageSelectors: [
        '.product-image-container',
        '.product-media',
        '.product-image-wrapper',
        '.card__media',
        '.media--square',
        '.product__media',
        '.product-image',
        '.product-photo',
        '[data-product-image]',
        '.card__content',       // Dawn theme
        '.media',               // Dawn theme
        '.card-information',    // Dawn theme
        '.grid-product__content' // Common pattern  
      ],
      
      // Animation delay for staggered badge appearance
      animationDelay: 100,
      
      // Enable debug logging
      debug: false,
      
      ...options
    };
    
    this.badgeCount = 0;
    this.init();
  }
  
  init() {
    this.log('Initializing Product Badge Overlay System...');
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupBadges());
    } else {
      this.setupBadges();
    }
    
    // Re-run when new products are loaded (infinite scroll, AJAX, etc.)
    this.observeProductChanges();
  }
  
  async setupBadges() {
    this.log('Setting up product badges...');
    
    // Find all product items on the page
    const productItems = this.findProductItems();
    this.log(`Found ${productItems.length} product items`);
    
    // Process all products concurrently
    const processingPromises = productItems.map((productItem, index) => 
      this.processProduct(productItem, index)
    );
    
    await Promise.all(processingPromises);
    
    this.log(`Added ${this.badgeCount} "One of One" badges`);
  }
  
  findProductItems() {
    const allProducts = [];
    
    // Try each selector to find products
    this.config.productSelectors.forEach(selector => {
      const products = document.querySelectorAll(selector);
      products.forEach(product => {
        // Avoid duplicates
        if (!allProducts.includes(product)) {
          allProducts.push(product);
        }
      });
    });
    
    return allProducts;
  }
  
  async processProduct(productItem, index) {
    try {
      let productData = this.extractProductData(productItem);
      
      if (!productData) {
        this.log('Could not extract product data from item');
        return;
      }
      
      this.log(`Processing product: ${productData.handle}`);
      
      // If no tags found in DOM, fetch from Shopify Product API
      if ((!productData.tags || productData.tags.length === 0) && productData.handle) {
        try {
          const response = await fetch(`/products/${productData.handle}.js`);
          if (response.ok) {
            const apiData = await response.json();
            productData.tags = apiData.tags || [];
            this.log(`Fetched ${productData.tags.length} tags from API for ${productData.handle}`);
          }
        } catch (error) {
          this.log(`Failed to fetch tags from API for ${productData.handle}: ${error.message}`);
        }
      }
      
      const badgeInfo = this.shouldShowBadge(productData);
      if (badgeInfo) {
        this.addBadgeToProduct(productItem, index, badgeInfo);
      }
    } catch (error) {
      this.log(`Error processing product: ${error.message}`);
    }
  }
  
  extractProductData(element) {
    try {
      // Try multiple methods to get product data
      let productData = null;
      
      // Method 1: Look for JSON-LD structured data
      const jsonLd = element.querySelector('script[type="application/ld+json"]');
      if (jsonLd) {
        try {
          const data = JSON.parse(jsonLd.textContent);
          if (data['@type'] === 'Product') {
            productData = {
              handle: this.extractHandleFromUrl(data.url),
              tags: data.keywords ? data.keywords.split(',').map(tag => tag.trim().toLowerCase()) : [],
              collections: [] // Will be extracted from other sources
            };
          }
        } catch (e) {
          // JSON parsing failed, continue to next method
        }
      }
      
      // Method 2: Look for product link URLs (Dawn theme compatibility)
      if (!productData) {
        const productLinks = element.querySelectorAll('a[href*="/products/"]');
        if (productLinks.length > 0) {
          const productUrl = productLinks[0].href;
          const handle = this.extractHandleFromUrl(productUrl);
          if (handle) {
            productData = {
              handle: handle,
              tags: [],
              collections: []
            };
          }
        }
      }
      
      // Method 3: Look for data attributes (various theme compatibility)
      if (!productData) {
        const possibleHandles = [
          element.dataset.productHandle,
          element.dataset.product,
          element.getAttribute('data-product-handle'),
          element.getAttribute('data-product-id'),
          element.querySelector('[data-product-handle]')?.dataset.productHandle,
          element.querySelector('[data-product]')?.dataset.product
        ].filter(Boolean);
        
        if (possibleHandles.length > 0) {
          productData = {
            handle: possibleHandles[0],
            tags: [],
            collections: []
          };
        }
      }
      
      // Method 4: Extract from form action (Dawn theme product forms)
      if (!productData) {
        const form = element.querySelector('form[action*="/cart/add"]');
        if (form) {
          const action = form.getAttribute('action');
          // Look for product ID in form, then try to get handle from window.meta if available
          const productIdInput = form.querySelector('input[name="id"]');
          if (productIdInput && window.meta && window.meta.product) {
            productData = {
              handle: window.meta.product.handle,
              tags: window.meta.product.tags || [],
              collections: []
            };
          }
        }
      }
      
      // Method 5: Fallback - extract from any URL pattern
      if (!productData) {
        const allLinks = element.querySelectorAll('a[href]');
        for (const link of allLinks) {
          const handle = this.extractHandleFromUrl(link.href);
          if (handle) {
            productData = {
              handle: handle,
              tags: [],
              collections: []
            };
            break;
          }
        }
      }
      
      // Extract collections from various sources
      if (productData) {
        // Look for collection context in current page URL
        const currentPath = window.location.pathname;
        if (currentPath.includes('/collections/')) {
          const collectionMatch = currentPath.match(/\/collections\/([^/]+)/);
          if (collectionMatch) {
            productData.collections.push(collectionMatch[1]);
          }
        }
        
        // Look for collection data attributes
        const collectionSources = [
          element.dataset.collection,
          element.getAttribute('data-collection'),
          element.querySelector('[data-collection]')?.dataset.collection
        ].filter(Boolean);
        
        collectionSources.forEach(collection => {
          if (!productData.collections.includes(collection)) {
            productData.collections.push(collection);
          }
        });
        
        if (this.config.debug) {
          console.log('[ProductBadgeOverlay] Extracted product data:', productData);
        }
      }
      
      return productData;
    } catch (error) {
      if (this.config.debug) {
        console.log('[ProductBadgeOverlay] Error extracting product data:', error);
      }
      return null;
    }
  }
  
  shouldShowBadge(productData) {
    // Check if product is in any target collections
    if (productData.hasTargetCollection) {
      return this.getBestBadgeForProduct(productData) || true;
    }
    
    // Check collections
    if (productData.collections && productData.collections.length > 0) {
      const hasTargetCollection = productData.collections.some(collection =>
        this.config.targetCollections.includes(collection.toLowerCase())
      );
      if (hasTargetCollection) {
        return this.getBestBadgeForProduct(productData) || true;
      }
    }
    
    // Check for priority tags that should trigger badges
    const bestBadge = this.getBestBadgeForProduct(productData);
    if (bestBadge) {
      this.log(`Product matched by priority tag: ${bestBadge.tag}`);
      return bestBadge;
    }
    
    return false;
  }
  
  // NEW METHOD: Find the best badge based on tag priority
  getBestBadgeForProduct(productData) {
    if (!productData.tags || productData.tags.length === 0) {
      return null;
    }
    
    // Convert tags to lowercase for comparison
    const productTags = productData.tags.map(tag => tag.toLowerCase());
    
    // Find the highest priority badge that matches
    let bestBadge = null;
    let highestPriority = 0;
    
    for (const badgeConfig of this.config.badgeHierarchy) {
      if (productTags.includes(badgeConfig.tag.toLowerCase()) && 
          badgeConfig.priority > highestPriority) {
        bestBadge = badgeConfig;
        highestPriority = badgeConfig.priority;
      }
    }
    
    return bestBadge;
  }
  
  addBadgeToProduct(productItem, index, badgeInfo = null) {
    // Find the image container
    const imageContainer = this.findImageContainer(productItem);
    
    if (!imageContainer) {
      this.log('Could not find image container for product');
      return;
    }
    
    // IMPROVED DUPLICATE PREVENTION
    // Check if badge already exists in multiple places
    if (imageContainer.querySelector('.product-badge-overlay') || 
        productItem.querySelector('.product-badge-overlay') ||
        productItem.classList.contains('has-one-of-one-badge')) {
      this.log('Badge already exists for this product - skipping');
      return;
    }
    
    // Create badge element with appropriate image
    const badge = this.createBadgeElement(badgeInfo);
    
    // PARENT CONTAINER FIX - Ensure proper positioning context
    const parentStyle = window.getComputedStyle(imageContainer);
    if (parentStyle.position === 'static') {
      imageContainer.style.position = 'relative';
      this.log('Fixed parent container positioning to relative');
    }
    
    // Add badge to image container
    imageContainer.appendChild(badge);
    
    // Add class to product item for CSS targeting
    productItem.classList.add('has-one-of-one-badge');
    
    // Animate badge appearance with staggered timing
    setTimeout(() => {
      badge.classList.add('fade-in');
    }, index * this.config.animationDelay);
    
    this.badgeCount++;
    this.log('Added badge to product');
  }
  
  findImageContainer(productItem) {
    // Try each image selector
    for (const selector of this.config.imageSelectors) {
      const container = productItem.querySelector(selector);
      if (container) {
        return container;
      }
    }
    
    // Fallback: look for any img tag and use its parent
    const img = productItem.querySelector('img');
    if (img && img.parentElement) {
      return img.parentElement;
    }
    
    return null;
  }
  
  createBadgeElement(badgeInfo = null) {
    const badge = document.createElement('div');
    badge.className = 'product-badge-overlay';
    badge.setAttribute('aria-hidden', 'true');
    
    // WORKING PRODUCT PAGE POSITIONING (from debug script)
    // Detect if we're on a product page and force positioning
    const isProductPage = this.isProductPage();
    if (isProductPage) {
      // Use the EXACT same approach that worked in the debug script
      badge.style.cssText = `
        position: absolute !important;
        bottom: 8px !important;
        right: 8px !important;
        top: auto !important;
        left: auto !important;
        width: 60px !important;
        height: 60px !important;
        z-index: 99999 !important;
        margin: 0 !important;
        padding: 0 !important;
        transform: none !important;
      `.replace(/\s+/g, ' ').trim();
      this.log('Applied WORKING debug script positioning - proven approach');
    }
    
    const img = document.createElement('img');
    const shopUrl = window.shopUrl || window.location.origin;
    
    // Determine which badge image to use based on priority
    let badgeImageName;
    if (badgeInfo && typeof badgeInfo === 'object' && badgeInfo.badge) {
      badgeImageName = badgeInfo.badge; // Use priority-based badge
      img.alt = `${badgeInfo.tag.charAt(0).toUpperCase() + badgeInfo.tag.slice(1).replace('-', ' ')} Item`;
    } else {
      badgeImageName = this.config.fallbackBadgeImagePath; // Use fallback
      img.alt = 'One of One - Unique Item';
    }
    
    // Try multiple image paths in order of preference
    const imagePaths = [
      `${shopUrl}/cdn/shop/files/${badgeImageName}`,  // Files section (most common)
      `${shopUrl}/assets/${badgeImageName}`,           // Assets folder
      `${shopUrl}/cdn/shop/t/1/assets/${badgeImageName}` // Legacy assets path
    ];
    
    img.loading = 'lazy';
    
    // Set initial source and add error handling to try fallbacks
    img.src = imagePaths[0];
    
    let currentPathIndex = 0;
    img.onerror = () => {
      currentPathIndex++;
      if (currentPathIndex < imagePaths.length) {
        this.log(`Badge image failed to load from: ${imagePaths[currentPathIndex - 1]}`);
        this.log(`Trying fallback path: ${imagePaths[currentPathIndex]}`);
        img.src = imagePaths[currentPathIndex];
      } else {
        this.log('All badge image paths failed to load:');
        imagePaths.forEach((path, index) => {
          this.log(`  ${index + 1}. ${path}`);
        });
      }
    };
    
    img.onload = () => {
      this.log(`Badge image loaded successfully from: ${img.src}`);
    };
    
    badge.appendChild(img);
    
    return badge;
  }
  
  extractHandleFromUrl(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }
  
  isProductPage() {
    // Multiple methods to detect product pages
    return (
      // Check URL path
      window.location.pathname.includes('/products/') ||
      // Check body classes (common Shopify patterns)
      document.body.classList.contains('template-product') ||
      document.body.className.includes('product') ||
      // Check for product page specific elements
      document.querySelector('.product-form') !== null ||
      document.querySelector('.product-single') !== null ||
      document.querySelector('[data-product-id]') !== null ||
      // Check meta tags
      document.querySelector('meta[property="og:type"][content="product"]') !== null
    );
  }

  observeProductChanges() {
    // Watch for dynamically loaded products (AJAX, infinite scroll)
    const observer = new MutationObserver((mutations) => {
      let hasNewProducts = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if new node contains products
            const hasProducts = this.config.productSelectors.some(selector =>
              node.matches && node.matches(selector) || 
              node.querySelector && node.querySelector(selector)
            );
            
            if (hasProducts) {
              hasNewProducts = true;
            }
          }
        });
      });
      
      if (hasNewProducts) {
        this.log('Detected new products, updating badges...');
        setTimeout(() => this.setupBadges(), 100);
      }
    });
    
    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  log(message) {
    if (this.config.debug) {
      console.log(`[ProductBadgeOverlay] ${message}`);
    }
  }
  
  // Public method to manually refresh badges
  refresh() {
    this.badgeCount = 0;
    this.setupBadges();
  }
  
  // Public method to update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.refresh();
  }
}

// Auto-initialize when script loads
document.addEventListener('DOMContentLoaded', () => {
  // Initialize with debug enabled (set to false in production)
  window.productBadgeOverlay = new ProductBadgeOverlay({
    debug: true // Set to false in production
  });
});

// Alternative asset URL construction for different Shopify setups
function getShopifyAssetUrl(filename) {
  const shopUrl = window.shopUrl || window.location.origin;
  // Try multiple potential asset paths
  const possiblePaths = [
    `${shopUrl}/cdn/shop/files/${filename}`,
    `${shopUrl}/files/${filename}`,
    `${shopUrl}/assets/${filename}`
  ];
  return possiblePaths[0]; // Use the first one as default
}

// Export for manual initialization if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductBadgeOverlay;
}
